import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import StartStrategyModal from "./StartStrategyModal";
import BrokerModal from "shared/Modal/BrokerModal";
import BrokerPortfolioModal from "shared/Modal/BrokerPortfolioModal";
import { AllocationAmountModal } from "shared/Modal/AllocationAmountModal";
import { parseErrorMessage, openNewTab } from "utils";
import { getBrokerOAuthURL } from "services";
import { toast } from "Layout/slice";
import { SnaptradeModal } from "shared/Modal/SnaptradeModal";
import { contains, isFreeUser } from "utils/index";
import { togglePurchasePlanModal } from "Layout/slice/index";
import { getNcbSettings, saveNcbSettings } from "features/Strategy/service";
import { ScheduleModal } from "features/Strategy/v2/NoCodeBuilder/Setting/ScheduleModal";
import { DEFAULT_TRADE_DAYS } from "utils/constants";

const StartStrategy = ({
  showBrokerModal,
  toggleBrokerModal,
  strategyId,
  strategyName = "",
  onStartedStrategy,
  skipBrokerModal = false,
  requireSchedule = false,
}) => {
  const dispatch = useDispatch();
  const [brokerId, setBrokerId] = useState(null);
  const [portfolioId, setPortfolioId] = useState(null);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);

  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [showAllocationAmt, setShowAllocationAmt] = useState(false);
  const [allocationPrice, setAllocationPrice] = useState(100);
  const [allocationAvailableBalance, setAllocationAvailableBalance] =
    useState(100);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showSnaptradeModal, setShowSnaptradeModal] = useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDays, setScheduleDays] = useState(DEFAULT_TRADE_DAYS);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  const isFreePlan = isFreeUser();

  useEffect(() => {
    if (!(skipBrokerModal && showBrokerModal)) return;
    // For no-code strategies, make the user confirm/edit the run schedule
    // before continuing into broker selection, so it never defaults to "None".
    if (requireSchedule) {
      setShowScheduleModal(true);
    } else {
      setShowPortfolioModal(true);
    }
  }, [showBrokerModal]);

  // Pre-fill the schedule modal with the saved schedule (or weekday defaults).
  useEffect(() => {
    if (!showScheduleModal) return;
    (async () => {
      try {
        const settings = await getNcbSettings(strategyId);
        const tradeDays = settings?.trade_days;
        setScheduleDays(
          Array.isArray(tradeDays) && tradeDays.length
            ? tradeDays
            : DEFAULT_TRADE_DAYS
        );
      } catch (error) {
        setScheduleDays(DEFAULT_TRADE_DAYS);
      }
    })();
  }, [showScheduleModal, strategyId]);

  const handleScheduleConfirm = async (tradeDays) => {
    setIsSavingSchedule(true);
    try {
      await saveNcbSettings(strategyId, {
        time_restrictions: true,
        trade_days: tradeDays,
      });
      setShowScheduleModal(false);
      setShowPortfolioModal(true);
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleScheduleCancel = () => {
    setShowScheduleModal(false);
    // Reset the deploy trigger so the user can start over.
    toggleBrokerModal();
  };

  const togglePortfolioModal = () => {
    setShowPortfolioModal(!showPortfolioModal);
    toggleBrokerModal(!showBrokerModal);
  };

  const toggleStrategyModal = () => setShowStrategyModal(!showStrategyModal);

  const toggleAllocationAmtModal = () => {
    setShowAllocationAmt(!showAllocationAmt);
    setShowStrategyModal(false);
  };

  const handlePortfolioClick = async (portfolio) => {
    if (isFreePlan && !contains(portfolio?.broker_name, "Surmount")) {
      dispatch(togglePurchasePlanModal(true));
    } else {
      togglePortfolioModal();
      setPortfolioId(
        !skipBrokerModal ? portfolio.id : portfolio.broker_account_id
      );
      toggleAllocationAmtModal();
      setShowStrategyModal(false);
    }
  };

  const handleBrokerSelect = async (broker) => {
    setBrokerId(broker.key);
    try {
      setIsConnecting(true);
      const response = await getBrokerOAuthURL(broker.key);
      if (response?.oauth_url) {
        openNewTab(response.oauth_url);
      } else {
        dispatch(toast({ body: "Account Connected Successfully!" }));
      }
    } catch (error) {
      setIsConnecting(false);
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsConnecting(false);
      toggleBrokerModal();
    }
    setShowPortfolioModal(true);
  };

  const handleAllocationBack = () => {
    toggleAllocationAmtModal();
    togglePortfolioModal();
  };

  const handleAllocationContinue = (price, availableBalance) => {
    toggleAllocationAmtModal();
    setAllocationPrice(price);
    setAllocationAvailableBalance(availableBalance);
    toggleStrategyModal();
  };

  const toggleSnaptradeModal = () => {
    setShowSnaptradeModal(!showSnaptradeModal);
  };

  const handleSnaptradeSuccess = () => {
    toggleSnaptradeModal();
    togglePortfolioModal();
  };

  return (
    <>
      <ScheduleModal
        show={showScheduleModal}
        onHide={handleScheduleCancel}
        initialDays={scheduleDays}
        onConfirm={handleScheduleConfirm}
        isSaving={isSavingSchedule}
      />

      {showBrokerModal && !skipBrokerModal && (
        <BrokerModal
          show={showBrokerModal}
          onHide={toggleBrokerModal}
          handleBrokerClick={handleBrokerSelect}
          isConnecting={isConnecting}
        />
      )}

      {showPortfolioModal && (
        <BrokerPortfolioModal
          brokerId={brokerId}
          show={showPortfolioModal}
          onHide={togglePortfolioModal}
          handlePortfolioClick={handlePortfolioClick}
          showPortfolioModal={() => setShowPortfolioModal(true)}
          skipBrokerModal={skipBrokerModal}
          toggleBrokerModal={toggleSnaptradeModal}
          strategyId={strategyId}
        />
      )}
      <AllocationAmountModal
        show={showAllocationAmt}
        showBackButton={true}
        onHide={toggleAllocationAmtModal}
        handleBack={handleAllocationBack}
        onContinue={handleAllocationContinue}
        accountId={portfolioId}
        strategyName={strategyName}
      />
      <StartStrategyModal
        show={showStrategyModal}
        onHide={toggleStrategyModal}
        strategyId={strategyId}
        portfolioId={portfolioId}
        allocationPrice={allocationPrice}
        allocationAvailableBalance={allocationAvailableBalance}
        onStartedStrategy={onStartedStrategy}
        strategyName={strategyName}
      />

      {showSnaptradeModal && (
        <SnaptradeModal
          show={showSnaptradeModal}
          onHide={toggleSnaptradeModal}
          onSuccess={handleSnaptradeSuccess}
        />
      )}
    </>
  );
};

StartStrategy.propTypes = {
  showBrokerModal: PropTypes.bool,
  toggleBrokerModal: PropTypes.func,
  strategyId: PropTypes.string,
  strategyName: PropTypes.string,
  onStartedStrategy: PropTypes.func,
  skipBrokerModal: PropTypes.bool,
  requireSchedule: PropTypes.bool,
};

export default StartStrategy;
